import * as React from 'react';
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  Box, Modal, Button, Stack, TextField, Typography,
  Divider, Alert, Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '45vw',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
  overflowY: 'auto',
  backgroundColor: '#f5f5f5',
};

export default function ModalProjetoConcluido({ open, onClose, proposta, setProposta, anexo, onAddAnexo }) {
  const [valorProjeto, setValorProjeto] = useState(proposta?.valorprojeto || '');

  const temNF = React.useMemo(
    () => anexo?.some((a) => a.tipo?.nome === 'Nota Fiscal de Projeto'),
    [anexo]
  );
  const temDiagrama = React.useMemo(
    () => anexo?.some((a) => a.tipo?.nome === 'Diagrama Unifilar'),
    [anexo]
  );
  const temValor = Number(valorProjeto) > 0;

  React.useEffect(() => {
    if (proposta?.valorprojeto) setValorProjeto(proposta.valorprojeto);
  }, [proposta?.valorprojeto]);

  const salvarValor = () => {
    setProposta((prev) => ({ ...prev, valorprojeto: parseFloat(valorProjeto) || 0 }));
  };

  const StatusItem = ({ label, ok }) => (
    <Stack direction="row" alignItems="center" gap={1} py={0.5}>
      {ok
        ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
        : <WarningAmberIcon sx={{ color: '#e65100', fontSize: 20 }} />}
      <Typography variant="body2" color={ok ? '#2e7d32' : '#e65100'}>
        {label}
      </Typography>
      {ok
        ? <Chip label="OK" size="small" color="success" sx={{ ml: 'auto' }} />
        : <Chip label="Pendente" size="small" color="warning" sx={{ ml: 'auto' }} />}
    </Stack>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6" fontWeight="bold" color="#e65100" mb={2}>
          Projeto Concluído — Pendências
        </Typography>

        <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
          Preencha os campos abaixo para avançar para a etapa de Envio para Montagem.
          Todos os três itens são obrigatórios.
        </Alert>

        {/* Status geral */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" mb={1}>Status</Typography>
          <StatusItem label="Valor do Projeto informado" ok={temValor} />
          <Divider />
          <StatusItem label="Nota Fiscal de Projeto anexada" ok={temNF} />
          <Divider />
          <StatusItem label="Diagrama Unifilar anexado" ok={temDiagrama} />
        </Box>

        {/* Valor do Projeto */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="#e65100" mb={1}>
            1. Valor do Projeto de Engenharia
          </Typography>
          <Stack direction="row" gap={2} alignItems="center">
            <TextField
              size="small"
              label="Valor (R$)"
              type="number"
              value={valorProjeto}
              onChange={(e) => setValorProjeto(e.target.value)}
              onBlur={salvarValor}
              sx={{ width: 200, backgroundColor: 'white' }}
              InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>R$</span> }}
            />
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={salvarValor}
              disabled={!valorProjeto || valorProjeto <= 0}
            >
              Salvar Valor
            </Button>
          </Stack>
        </Box>

        {/* Nota Fiscal */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="#e65100" mb={1}>
            2. Nota Fiscal de Projeto
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Selecione o tipo "Nota Fiscal de Projeto" ao anexar. Visível apenas para supervisor e quem anexou.
          </Typography>
          {temNF
            ? <Chip label="NF já anexada ✓" color="success" size="small" />
            : (
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => onAddAnexo('nf')}
                sx={{ borderColor: '#e65100', color: '#e65100' }}
              >
                Adicionar Nota Fiscal
              </Button>
            )}
        </Box>

        {/* Diagrama Unifilar */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="#e65100" mb={1}>
            3. Diagrama Unifilar
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Selecione o tipo "Diagrama Unifilar" ao anexar.
          </Typography>
          {temDiagrama
            ? <Chip label="Diagrama já anexado ✓" color="success" size="small" />
            : (
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => onAddAnexo('diagrama')}
                sx={{ borderColor: '#e65100', color: '#e65100' }}
              >
                Adicionar Diagrama Unifilar
              </Button>
            )}
        </Box>

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={onClose}
            color={temValor && temNF && temDiagrama ? 'success' : 'inherit'}
          >
            {temValor && temNF && temDiagrama ? 'Concluído ✓' : 'Fechar'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}

ModalProjetoConcluido.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  proposta: PropTypes.object,
  setProposta: PropTypes.func,
  anexo: PropTypes.array,
  onAddAnexo: PropTypes.func,
};